import * as SnackbarEventAction from '../../actions/snackbarEvent';
import { AvatarWarningIcon } from './icons';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  useTheme
} from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactNode } from 'react';
import { WikusButton } from './button';
import { useActions } from '../../actions';

export type WikusConfirmationProps = Omit<DialogProps, 'buttons' | 'onAbort' | 'classes'> & {
  title: ReactNode;
  children: ReactNode;
  onConfirm?: () => void;
  message?: string;
  onAbort?: () => void;
  severity?: 'warning' | 'error';
};

const WikusConfirmation = ({
  children,
  title,
  onConfirm,
  onAbort,
  message,
  severity,
  ...props
}: WikusConfirmationProps) => {
  const snackbarActions: typeof SnackbarEventAction = useActions(SnackbarEventAction);
  const intl = useIntl();
  const theme = useTheme();

  const onConfirmInternal: typeof onConfirm = async () => {
    const result = await onConfirm?.();
    if (message && typeof result === 'boolean') {
      if (result) {
        snackbarActions.addSnackbarEvent({
          message: intl.formatMessage({
            id: `success.${message}`,
          }),
          severity: 'success',
        });
        props.onClose?.({}, 'backdropClick');
      } else {
        snackbarActions.addSnackbarEvent({
          message: intl.formatMessage({
            id: `errors.${message}`,
          }),
          severity: 'error',
        });
      }
    }

    return result;
  };
                  
  let severityColor =  theme.palette[severity || 'primary'].main;
  return (
    <Dialog maxWidth="xs" aria-labelledby="confirmation-dialog-title" {...props}>
      <DialogTitle id="confirmation-dialog-title">
        {severity ? (
          <Box display="flex" alignItems="center" style={{gap: '16px'}}>
            {!!severity && <AvatarWarningIcon />}
            <Box>{title}</Box>
          </Box>
        ) : (
          title
        )}
      </DialogTitle>
      <DialogContent dividers>{children}</DialogContent>
      <DialogActions>
        <WikusButton autoFocus onClick={onAbort} color="primary" variant="outlined">
          <FormattedMessage id="confirmation.abort" />
        </WikusButton>
        <WikusButton
          onClick={onConfirmInternal}
          color="primary"
          style={
            severity
              ? {
                  backgroundColor: severityColor,
                }
              : undefined
          }
        >
          <FormattedMessage id="confirmation.confirm" />
        </WikusButton>
      </DialogActions>
    </Dialog>
  );
};
// set for storybook to render html preview correctly
WikusConfirmation.displayName = 'WikusConfirmation';

export { WikusConfirmation };
